import {type FC, type RefObject, useCallback, useRef} from 'react';

import type {GetComponentProps, Nullable} from '../../utils/utility-types';
import type {NumberTextInput} from '../NumberTextInput';
import type {TextInput} from '../TextInput';
import type {Ref} from '../TextInput/types';
import type {Props} from './types';

import {eventPayloadActionCreator} from '../../utils/events';
import * as S from './styles';

const focus = (input: RefObject<Nullable<Ref>>) => {
  input.current?.focus();

  requestAnimationFrame(() => {
    input.current?.select();
  });
};

export const DurationTextInput: FC<Props> = ({
  hoursValue,
  minutesValue,
  onChange,
  ...rest
}) => {
  const hoursInputRef = useRef<Ref>(null);
  const minutesInputRef = useRef<Ref>(null);

  const onHoursChange: GetComponentProps<typeof NumberTextInput>['onChange'] =
    useCallback(
      e => {
        onChange(
          eventPayloadActionCreator(
            {text: `${e.payload?.text || 0}:${minutesValue}`},
            e.event,
          ),
        );
      },
      [minutesValue, onChange],
    );

  const onMinutesChange: GetComponentProps<typeof NumberTextInput>['onChange'] =
    useCallback(
      e => {
        onChange(
          eventPayloadActionCreator(
            {text: `${hoursValue}:${e.payload?.text || 0}`},
            e.event,
          ),
        );
      },
      [hoursValue, onChange],
    );

  const onHoursFocus = useCallback(() => {
    hoursInputRef.current?.select();
  }, []);

  const onMinutesFocus = useCallback(() => {
    minutesInputRef.current?.select();
  }, []);

  const onHoursKeyPress: GetComponentProps<typeof TextInput>['onKeyPress'] =
    useCallback(e => {
      if (e.payload?.key === 'ArrowRight') {
        focus(minutesInputRef);
      }

      if (e.payload?.key === 'ArrowLeft') {
        focus(hoursInputRef);
      }
    }, []);

  const onMinutesKeyPress: GetComponentProps<typeof TextInput>['onKeyPress'] =
    useCallback(e => {
      if (e.payload?.key === 'ArrowLeft') {
        focus(hoursInputRef);
      }

      if (e.payload?.key === 'ArrowRight') {
        focus(minutesInputRef);
      }
    }, []);

  return (
    <S.Container>
      <S.TextInput
        onChange={onHoursChange}
        onFocus={onHoursFocus}
        onKeyPress={onHoursKeyPress}
        ref={hoursInputRef}
        value={hoursValue.padStart(2, '0')}
        webProps={{
          min: 0,
        }}
        {...rest}
      />
      <S.Separator isDisabled={rest.disabled}>:</S.Separator>
      <S.TextInput
        isMinutes
        onChange={onMinutesChange}
        onFocus={onMinutesFocus}
        onKeyPress={onMinutesKeyPress}
        ref={minutesInputRef}
        value={minutesValue.padStart(2, '0')}
        webProps={{
          min: 0,
          step: 15,
        }}
        {...rest}
      />
    </S.Container>
  );
};
